import { useRef, useMemo } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'

/**
 * Chapter 13: The Normal Map (Integration)
 * Nested icosahedron + octahedron
 * 13 mini spheres orbiting (representing chapters in different colors)
 * White/gold color scheme
 * Different rotation speeds per layer
 */
export default function IntegratedFractal({ audioData, isActive, position = [0, 0, 0] }) {
  const icosahedronRef = useRef()
  const octahedronRef = useRef()
  const innerSphereRef = useRef()
  const orbitingSpheresRef = useRef([])

  const { fft, bass, mids, highs, amplitude, beat } = audioData || {
    fft: new Float32Array(128).fill(0),
    bass: 0,
    mids: 0,
    highs: 0,
    amplitude: 0,
    beat: 0
  }

  // Chapter colors for orbiting spheres
  const chapterColors = [
    '#FFD700', // 1: Gold
    '#4169E1', // 2: Royal Blue
    '#00FFFF', // 3: Cyan
    '#FF1493', // 4: Deep Pink
    '#9370DB', // 5: Medium Purple
    '#32CD32', // 6: Lime Green
    '#FF6347', // 7: Tomato
    '#00FF88', // 8: Spring Green
    '#FF6B6B', // 9: Coral
    '#FF69B4', // 10: Hot Pink
    '#00FFFF', // 11: Cyan
    '#8B00FF', // 12: Purple
    '#FFFFFF', // 13: White (integration)
  ]

  // Orbiting sphere positions (distributed on sphere)
  const orbitPositions = useMemo(() => {
    const positions = []
    const phi = Math.PI * (3 - Math.sqrt(5))

    for (let i = 0; i < 13; i++) {
      const y = 1 - (i / 12) * 2
      const radius = Math.sqrt(1 - y * y)
      const theta = phi * i

      positions.push({
        x: Math.cos(theta) * radius,
        y: y,
        z: Math.sin(theta) * radius,
        angle: theta,
        speed: 0.3 + i * 0.05
      })
    }

    return positions
  }, [])

  useFrame((state, delta) => {
    if (!isActive) return

    const time = state.clock.elapsedTime

    if (icosahedronRef.current) {
      // Outer icosahedron - slow rotation
      icosahedronRef.current.rotation.y += delta * 0.1 + amplitude * 0.02
      icosahedronRef.current.rotation.x = Math.sin(time * 0.2) * 0.2

      // Scale with bass
      const scale = 3 + bass * 0.3
      icosahedronRef.current.scale.setScalar(scale)
    }

    if (octahedronRef.current) {
      // Middle octahedron - medium rotation (opposite direction)
      octahedronRef.current.rotation.y -= delta * 0.08 + amplitude * 0.03
      octahedronRef.current.rotation.z += delta * 0.07

      // Scale with mids
      const scale = 2 + mids * 0.25
      octahedronRef.current.scale.setScalar(scale)
    }

    if (innerSphereRef.current) {
      // Inner sphere - fast rotation
      innerSphereRef.current.rotation.y += delta * 0.12 + amplitude * 0.05
      innerSphereRef.current.rotation.x += delta * 0.1

      // Scale and glow with highs
      const scale = 1 + highs * 0.4
      innerSphereRef.current.scale.setScalar(scale)
      innerSphereRef.current.material.emissiveIntensity = 2 + highs * 2
    }

    // Animate orbiting spheres
    orbitingSpheresRef.current.forEach((sphere, i) => {
      if (!sphere) return

      const orbitData = orbitPositions[i]
      const orbitRadius = 4

      // Each sphere orbits at different speed
      const angle = time * orbitData.speed + orbitData.angle

      sphere.position.x = Math.cos(angle) * orbitRadius * orbitData.x
      sphere.position.y = orbitData.y * orbitRadius
      sphere.position.z = Math.sin(angle) * orbitRadius * orbitData.z

      // Rotate sphere itself
      sphere.rotation.y += delta * (0.3 + i * 0.03)

      // React to different FFT bands
      const fftIndex = Math.floor((i / 13) * fft.length)
      const fftValue = fft[fftIndex] || 0

      const scale = 0.15 + fftValue * 0.15
      sphere.scale.setScalar(scale)

      // Emissive intensity
      sphere.material.emissiveIntensity = 0.8 + fftValue * 1.5

      // Beat pulse
      if (beat > 0.5) {
        sphere.scale.setScalar(0.25)
      }
    })
  })

  return (
    <group position={position}>
      {/* Outer icosahedron wireframe */}
      <mesh ref={icosahedronRef}>
        <icosahedronGeometry args={[3, 0]} />
        <meshBasicMaterial
          color="#FFD700"
          wireframe
          transparent
          opacity={0.4}
        />
      </mesh>

      {/* Middle octahedron */}
      <mesh ref={octahedronRef}>
        <octahedronGeometry args={[2, 0]} />
        <meshStandardMaterial
          color="#FFFFFF"
          emissive="#FFD700"
          emissiveIntensity={0.6}
          transparent
          opacity={0.7}
          metalness={0.8}
          roughness={0.2}
          wireframe
        />
      </mesh>

      {/* Inner glowing sphere */}
      <mesh ref={innerSphereRef}>
        <sphereGeometry args={[1, 32, 32]} />
        <meshStandardMaterial
          color="#FFFFFF"
          emissive="#FFD700"
          emissiveIntensity={2}
          transparent
          opacity={0.9}
        />
      </mesh>

      {/* 13 orbiting chapter spheres */}
      {chapterColors.map((color, i) => (
        <mesh
          key={`orbit-${i}`}
          ref={(el) => (orbitingSpheresRef.current[i] = el)}
        >
          <sphereGeometry args={[0.15, 16, 16]} />
          <meshStandardMaterial
            color={color}
            emissive={color}
            emissiveIntensity={0.8}
            transparent
            opacity={0.95}
            metalness={0.6}
            roughness={0.3}
          />

          {/* Mini point light for each sphere */}
          <pointLight
            position={[0, 0, 0]}
            intensity={0.5}
            color={color}
            distance={1.5}
          />
        </mesh>
      ))}

      {/* Central light */}
      <pointLight position={[0, 0, 0]} intensity={3} color="#FFD700" distance={12} />

      {/* Outer ambient glow */}
      <mesh>
        <sphereGeometry args={[5, 32, 32]} />
        <meshBasicMaterial
          color="#FFD700"
          transparent
          opacity={0.03}
          side={THREE.BackSide}
          blending={THREE.AdditiveBlending}
        />
      </mesh>
    </group>
  )
}
